import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner, BlockStack } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { useParams } from "react-router-dom";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { removeSigner } from "services/dokobit.service";
import { signerEditErrorKey } from "utils/signer-edit-errors";
import { toast } from "sonner";

interface DokobitSignerRemoveConfirmProps {
  fileId: string;
  signatureId: string;
  signerName?: string;
  signedCount?: number;
  totalCount?: number;
}

const DokobitSignerRemoveConfirm: React.FC<DokobitSignerRemoveConfirmProps> = ({
  fileId,
  signatureId,
  signerName,
  signedCount = 0,
  totalCount = 0,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId, orderId } = useParams();

  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleConfirm = async () => {
    if (!companyId || !fileId || !signatureId) return;
    try {
      setIsLoading(true);
      await removeSigner(companyId, { fileId, signatureId });
      toast.success(t("cancel_signer_request_success"));
      if (orderId) {
        dispatch(fetchOrderDetails({ companyId, orderId }));
      } else {
        dispatch(fetchFileDetails({ fileId, companyId }));
      }
      closeModal();
    } catch (e: any) {
      setErrMsg(
        signerEditErrorKey(e, e?.response?.data?.message || e?.message || "error")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
      </div>
      <BlockStack gap="400">
        <h1 style={{ textAlign: "center" }}>
          {t("cancel_signer_request_confirm", { name: signerName || "" })}
        </h1>

        {signedCount > 0 && (
          <Banner tone="info">
            {t("cancel_signer_request_signatures_kept", {
              signed: signedCount,
              total: totalCount,
            })}
          </Banner>
        )}

        <InlineStack align="space-between">
          <Button
            onClick={handleConfirm}
            loading={isLoading}
            tone="critical"
            variant="primary"
          >
            {t("Yes")}
          </Button>

          <Button onClick={closeModal}>{t("No")}</Button>
        </InlineStack>
      </BlockStack>
    </>
  );
};

export default DokobitSignerRemoveConfirm;
